// Shared weather constants used by both WeatherMap (home) and UnifiedMap (/map)

export const STATUS_COLORS = {
  producing: '#16A34A',
  not_producing: '#6B7280',
  starting_soon: '#3b82f6',
  ending_soon: '#f97316',
};

export const STATUS_LABELS = {
  producing: 'Producing',
  not_producing: 'Not Producing',
  starting_soon: 'Starting Soon',
  ending_soon: 'Ending Soon',
};


// Stable id to pair a region's side-panel card with its map marker (hover-to-enlarge).
// Uses commodity + coordinates so it matches whether the object comes from the
// harvest-alerts list or the marker map.
export { regionMarkerId } from '../../../utils/regionMarkerId';

export const OPENWEATHERMAP_API_KEY = import.meta.env.VITE_OPENWEATHERMAP_API_KEY || '';

export const API_MODE = 'paid';

// ---------- Weather forecast horizon ----------
// OpenWeather Maps 2.0 serves a 10-day forecast that is refreshed once per day
// near 03:00 UTC. The horizon is anchored on the LAST refresh boundary, NOT on
// the user's mount time, so a user mounting late in the day has fewer than
// 240 hours of valid tiles ahead. Without aligning the timeline to the real
// forecast end, playback drifts past the data and renders no tiles for the
// remaining frames.
export const FORECAST_REFRESH_HOUR_UTC = 3; // empirical: OWM refresh ~03:00 UTC
export const FORECAST_HORIZON_HOURS = 240; // 10-day window OWM serves after refresh
export const FORECAST_STEP_HOURS = 3;       // paid tier bucket size

/**
 * Compute the maximum playable hour for the timeline, given the mount time.
 *
 * Logic:
 *   1. Find the last refresh boundary at-or-before mount: today 03:00 UTC
 *      if mount is at or after that, otherwise yesterday 03:00 UTC.
 *   2. forecastEnd = lastRefresh + 240h.
 *   3. Floor mountDate to its 3h bucket (matches AnimatedWeatherLayer's
 *      `getCurrentUnixTime` floor before requesting tiles).
 *   4. maxHours = (forecastEnd - mountBucket) / 3600 — already a multiple of
 *      FORECAST_STEP_HOURS because both endpoints are on the 3h grid.
 */
export const computeForecastMaxHours = (mountDate = new Date()) => {
  const stepMs = FORECAST_STEP_HOURS * 3600 * 1000;
  const mountMs = mountDate.getTime();
  const mountBucketMs = Math.floor(mountMs / stepMs) * stepMs;

  const refreshEnd = new Date(mountMs);
  refreshEnd.setUTCHours(FORECAST_REFRESH_HOUR_UTC, 0, 0, 0);
  if (refreshEnd.getTime() > mountMs) {
    refreshEnd.setUTCDate(refreshEnd.getUTCDate() - 1);
  }
  const forecastEndMs = refreshEnd.getTime() + FORECAST_HORIZON_HOURS * 3600 * 1000;

  const remainingHours = Math.max(0, (forecastEndMs - mountBucketMs) / 3600000);
  const fullHours = Math.floor(remainingHours / FORECAST_STEP_HOURS) * FORECAST_STEP_HOURS;
  // Trim the last bucket: empirically the OWM forecast's final 3h slot is
  // unreliable (users see a layer-less frame at the slider's right edge if
  // we keep it). One-bucket safety margin matches the observed data end.
  return Math.max(0, fullHours - FORECAST_STEP_HOURS);
};

export const toInitCap = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
};
